/**
 * NavigationDrawer Component
 *
 * Specialized drawer component for navigation sidebar.
 * Encapsulates navigation-specific styling including paper background,
 * layout, and dimensions.
 *
 * Behavior: collapses to an icons-only rail by default, expands to the full
 * width on hover. The root width animates in lockstep with the paper so main
 * content is pushed aside — the rail never sits on top of page content (e.g.
 * the page banner logo).
 */

import { Drawer, DrawerProps } from '@mui/material';
import React, { createContext, useContext, useState } from 'react';

const DRAWER_WIDTH = 240;
const COLLAPSED_WIDTH = 72;
// Material's "standard" easing — accelerates in, decelerates out — reads
// noticeably smoother than the browser-default `ease`. 280ms is the sweet
// spot for a panel of this size: long enough to feel deliberate, short
// enough that fast mouse movement still keeps up.
const TRANSITION_MS = 280;
const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

interface NavigationDrawerProps extends Omit<DrawerProps, 'variant'> {
  variant?: 'permanent' | 'persistent' | 'temporary';
}

// Lets children (logo, user menu, etc.) react to collapsed/expanded state
// without each one re-deriving it from the DOM.
const SidebarExpandedContext = createContext(false);
export function useSidebarExpanded(): boolean {
  return useContext(SidebarExpandedContext);
}

const NavigationDrawer = React.forwardRef<HTMLDivElement, NavigationDrawerProps>(({ children, sx, ...props }, ref) => {
  const [expanded, setExpanded] = useState(false);
  const currentWidth = expanded ? DRAWER_WIDTH : COLLAPSED_WIDTH;

  return (
    <Drawer
      ref={ref}
      variant="permanent"
      sx={{
        // Root width animates with the paper so main content is pushed aside
        // rather than overlaid — keeps the page banner logo fully visible.
        width: currentWidth,
        flexShrink: 0,
        transition: `width ${TRANSITION_MS}ms ${EASING}`,
        '& .MuiDrawer-paper': {
          width: currentWidth,
          boxSizing: 'border-box',
          bgcolor: 'rgb(250, 250, 250)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          color: 'black',
          height: '100vh',
          zIndex: 100,
          overflowX: 'hidden',
          // Prevent label text from wrapping mid-transition.
          whiteSpace: 'nowrap',
          transition: `width ${TRANSITION_MS}ms ${EASING}`,
        },
        ...sx,
      }}
      PaperProps={{
        onMouseEnter: () => setExpanded(true),
        onMouseLeave: () => setExpanded(false),
      }}
      {...props}
    >
      <SidebarExpandedContext.Provider value={expanded}>
        {children}
      </SidebarExpandedContext.Provider>
    </Drawer>
  );
});

NavigationDrawer.displayName = 'NavigationDrawer';

export default NavigationDrawer;
export { DRAWER_WIDTH, COLLAPSED_WIDTH };
